import { useMemo, useState } from 'react';
import { Control } from 'react-hook-form';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { PopperContent } from '@/components/poppers/PopperContent';
import { DocumentDetailsFormShape } from '@/modules/documents/DocumentDetailsForm/DocumentDetailsForm.types';
import {
  PossibleChildClientProfileFragment,
  useGetChildrenQuery,
} from '@/modules/documents/DocumentDetailsForm/graphql/DocumentDetailsForm.generated';
import { ASSOCIATED_INDIVIDUAL_POPPER_CONTENT } from '@/modules/documents/documents.constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { is18OrOlder } from '@/modules/missingDocuments/missingDocuments.utils';
import { DocumentType } from '@/types/schema';
import { PathsOf } from '@/types/subform';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

interface DocumentAssociatedIndividualPickerProps {
  control: Control<DocumentDetailsFormShape>;
  selectedDocumentKind: '' | DocumentType;
}

export function DocumentAssociatedIndividualPicker({
  control,
  selectedDocumentKind,
}: DocumentAssociatedIndividualPickerProps) {
  const { primaryClients, householdId } = useHouseholdDetailsContext();
  const [possibleChildren, setPossibleChildren] = useState<
    PossibleChildClientProfileFragment[]
  >([]);
  useGetChildrenQuery({
    fetchPolicy: 'cache-and-network',
    skip: !householdId,
    variables: {
      householdId: householdId!,
    },
    onCompleted: (data) => {
      const possibleChildren = getNodes(data?.clientProfiles) ?? [];
      const adultChildren = possibleChildren.filter((cp) =>
        is18OrOlder(cp.dateOfBirth)
      );
      setPossibleChildren(adultChildren);
    },
    onError: (error) => {
      diagnostics.error(error.message);
    },
  });

  const clientProfileOptions: SelectInputOption<string>[] = useMemo(() => {
    const primaryClientOptions = (primaryClients ?? []).map((client) => ({
      value: client.id,
      display: client.displayName,
    }));

    // For healthcare proxy documents, also include minor children
    if (selectedDocumentKind === DocumentType.HealthcareProxy) {
      const childrenOptions = (possibleChildren ?? []).map((client) => ({
        value: client.id,
        display: client.displayName,
      }));

      return [...primaryClientOptions, ...childrenOptions];
    }

    return primaryClientOptions;
  }, [possibleChildren, primaryClients, selectedDocumentKind]);

  return (
    <FormAwareTypeaheadSelectInput<DocumentDetailsFormShape>
      options={clientProfileOptions}
      placeholder="Search individuals"
      control={control}
      label="Associated individual"
      fieldName={
        'associatedClientProfileId' as const satisfies PathsOf<DocumentDetailsFormShape>
      }
      contextualHelp={
        <PopperContent body={ASSOCIATED_INDIVIDUAL_POPPER_CONTENT} />
      }
    />
  );
}
